import type { FormikProps } from 'formik';
import { Box, Stack, Grid, Card } from '@mui/material';

import InfoCard from '../../../components/info-card';
import { AssessmentDetail, Candidate, TestReview, TotalSummary, TermsAndConditions } from '../../../components/shared';
import { type CandidateProps, type RequestProps, type SupplierProps, Test } from '../../../models';
import { useGetTestsQuery } from '../../../slices/apis/testApi';
import { useGetSupplierQuery } from '../../../slices/apis/testingCentreApi';
import { PricingCalculator } from '../../../utils/pricingCalculator';
import RequestDetailSkeleton from '../RequestDetailSkeleton';

interface RequestReviewProps {
	formik: FormikProps<{
		supplierId?: string;
		tests: { id: string }[];
		prefAssessmentDates: (Date | null)[];
		candidate?: CandidateProps;
		tncAccepted: boolean;
		extraInfo?: string | null;
	}>;
}

interface SingleRequestDetailProps {
	request: RequestProps & { supplier?: Pick<SupplierProps, 'name' | 'address'> };
	formik?: FormikProps<{ tncAccepted: boolean }>;
}

const RequestDetail: React.FC<SingleRequestDetailProps> = ({ request, formik }) => {
	const { candidate, tests, supplier, prefAssessmentDates } = request;

	const normalisedTests = tests.map(Test.normalise);

	const price = new PricingCalculator(normalisedTests);

	return (
		<Box>
			<Grid container spacing={3}>
				<Grid size={{ xs: 12, md: 7 }}>
					<Stack spacing={3}>
						<InfoCard title="Assessment Details">
							<AssessmentDetail
								location={supplier?.address as string}
								assessmentDates={prefAssessmentDates}
								dateLineText="Preferred"
							/>
						</InfoCard>
						<InfoCard title="Candidate Details">
							<Candidate candidate={candidate} />
						</InfoCard>

						<InfoCard title="Item Details">
							<TestReview tests={normalisedTests} />
						</InfoCard>
					</Stack>
				</Grid>

				<Grid size={{ xs: 12, md: 5 }}>
					<InfoCard title="Summary">
						<TotalSummary total={price.total()} subtotal={price.subtotal()} gst={price.gst()} />
					</InfoCard>
				</Grid>
			</Grid>
			{formik && <TermsAndConditions formik={formik} />}
		</Box>
	);
};

export const RequestReview: React.FC<RequestReviewProps> = ({ formik }) => {
	const requestToReview = {} as RequestProps & { supplier?: Pick<SupplierProps, 'name' | 'address'> };

	const { data: supplier } = useGetSupplierQuery(formik.values.supplierId);
	const { data: allTests } = useGetTestsQuery();

	if (supplier) {
		requestToReview.supplier = supplier;
	}

	if (allTests) {
		requestToReview.tests = allTests.filter(
			(test) => formik.values.tests.findIndex((t) => t.id === test.id.toString()) > -1,
		);
	}

	if (formik.values.extraInfo) {
		requestToReview.extraInfo = formik.values.extraInfo;
	}

	requestToReview.prefAssessmentDates = formik.values.prefAssessmentDates.filter((date): date is Date => date !== null);

	if (formik.values.candidate) {
		requestToReview.candidate = { ...formik.values.candidate };
	}

	return requestToReview.supplier && requestToReview.tests ? (
		<Card>
			<RequestDetail request={requestToReview} formik={formik} />
		</Card>
	) : (
		<RequestDetailSkeleton />
	);
};
